import React, { useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './Footer.css';

// Anti-scrape: decode contact info at runtime so bots can't read plaintext from source
const decode = (parts: string[]) => parts.join('');

const Footer: React.FC = () => {
  const { language, t } = useLanguage();

  const contact = useMemo(() => ({
    street: decode(['Nieuwezijds', ' voorburgwal', ' 334', '-2']),
    city: decode(['Amster', 'dam']),
    mapQuery: decode(['Nieuwezijds', '+voorburgwal', '+334-2', ',+Amsterdam']),
    phone: decode(['020', ' 308', ' 62', '71']),
    phoneHref: decode(['tel:', '+31', '20', '308', '6271']),
  }), []);

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          {/* Logo and Contact Section */}
          <div className="footer-section footer-main">
            <div className="footer-logo">
              <img
                src="/assets/logos/Logo secundair.png"
                alt="Meijer & Knijnenberg"
                className="footer-logo-image"
              />
            </div>
            <div className="footer-contact-info">
              <a href={`https://maps.google.com/?q=${contact.mapQuery}`}
                 className="footer-contact-item"
                 target="_blank"
                 rel="noopener noreferrer">
                <svg className="footer-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <div>
                  <div>{contact.street}</div>
                  <div>{contact.city}</div>
                </div>
              </a>
              <a href={contact.phoneHref} className="footer-contact-item">
                <svg className="footer-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <span>{contact.phone}</span>
              </a>
              <a href="mailto:info@meijerknijnenberg.com" className="footer-contact-item">
                <svg className="footer-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <span>info@meijerknijnenberg.com</span>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div className="footer-section">
            <h3 className="footer-heading">
              {language === 'nl' ? 'Bedrijf' : 'Company'}
            </h3>
            <ul className="footer-links">
              <li><a href="/about">{language === 'nl' ? 'Over Ons' : 'About'}</a></li>
              <li><a href="/team">{t('nav.ourTeam')}</a></li>
              <li><a href="/partners">{t('nav.ourPartners')}</a></li>
              <li><a href="/success-stories">{language === 'nl' ? 'Succesverhalen' : 'Success Stories'}</a></li>
              <li><a href="/jobs">{language === 'nl' ? 'Vacatures' : 'Vacancies'}</a></li>
            </ul>
          </div>

          {/* Support Links */}
          <div className="footer-section">
            <h3 className="footer-heading">Support</h3>
            <ul className="footer-links">
              <li><a href="/#faq">{language === 'nl' ? 'Veelgestelde Vragen' : 'FAQs'}</a></li>
              <li><a href="/resources">{language === 'nl' ? 'Bronnen' : 'Resources'}</a></li>
              <li><a href="/about#team">{t('nav.contact')}</a></li>
            </ul>
          </div>

          {/* Services Links */}
          <div className="footer-section">
            <h3 className="footer-heading">
              {language === 'nl' ? 'Wat wij doen' : 'What We Do'}
            </h3>
            <ul className="footer-links">
              <li><a href="/jobs?discipline=Accounting">{t('sectors.accounting')}</a></li>
              <li><a href="/jobs?discipline=Tax">{t('sectors.tax')}</a></li>
              <li><a href="/jobs?discipline=Audit">{t('sectors.audit')}</a></li>
              <li><a href="/jobs?discipline=Legal">{t('sectors.legal')}</a></li>
              <li><a href="/jobs">{language === 'nl' ? 'Bekijk alles' : 'View All'}</a></li>
            </ul>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="footer-copyright">{t('footer.rights')}</p>
            <div className="footer-legal">
              <a href="/privacy">{t('footer.privacy')}</a>
              <span className="footer-divider">|</span>
              <a href="/cookies">{t('footer.cookies')}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
